import {
  DownloadIcon,
  KebabHorizontalIcon,
  PencilIcon,
  PlusIcon,
  SyncIcon,
  TrashIcon,
  UploadIcon
} from "@primer/octicons-react";
import {
  ActionList,
  ActionMenu,
  Box,
  IconButton,
  useConfirm,
  Text as PrimerText,
  Dialog,
  Label
} from "@primer/react";
import { useCallback, useEffect, useState } from "react";
import Button from "../../components/ui/button";
import DataTable from "../../components/ui/data-table";
import TextInput from "../../components/ui/text-input";
import Text from "../../components/ui/text";
import { useCommonContext } from "../../contexts/common";
import { NotifyHelper } from "../../helpers/toast";

import moment from "moment";
import { lichSuXuongPhongYTeApi } from "../../api/lichSuXuongPhongYTeApi";
import { danThuocApi } from "../../api/danThuocApi";
import { useCommonSelectedHook } from "../../hooks/useCommonSelectedHook";
import { ILichSuXuongPhongYTe } from "../../models/response/crm-hocsinh/ILichSuXuongPhongYTe";
import LichSuXuongPhongYTeModal from "./LichSuXuongPhongYTeModal";
import ImportLichSuYTeModal from "./import-lich-su-y-te-modal/ImportLichSuYTeModal";
import TextTranslated from "../../components/text";
import HocSinhProfile from "../hoc-sinh-profile/HocSinhProfile";
import ComboboxLichSuYTeType from "../../components/combobox-lich-su-y-te-type";
import { ComboboxTruong } from "../../components/combobox-truong";

import TextWithEllipse from "../../components/ui/text-with-ellipse";
import HocSinhShortInfo from "../../components/hoc-sinh/HocSinhShortInfo";
import HocSinhShortInfoWithClass from "../../components/hoc-sinh/HocSinhShortInfoWithClass";
import { IFaDanThuocItemRepsone } from "../../models/response/dan-thuoc/IDanThuocItemRepsone";

const LichSuXuongPhongYTePage = () => {
  const [isShowModal, setIsShowModal] = useState(false);
  const [selectedId, setSelectedId] = useState(0);
  const [lichSuXuongPhongYTes, setLichSuXuongPhongYTes] = useState<ILichSuXuongPhongYTe[]>([]);
  const [allLichSuXuongPhongYTes, setAllLichSuXuongPhongYTes] = useState<ILichSuXuongPhongYTe[]>([]);
  const { translate } = useCommonContext();
  const confirm = useConfirm();
  const [isLoading, setIsLoading] = useState(false);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const { nam_hoc, dm_coso_id } = useCommonSelectedHook();

  // Filter states for dặn thuốc - chỉ lấy ngày hiện tại
  const [danThuocData, setDanThuocData] = useState<(IFaDanThuocItemRepsone & { isConfirmed?: boolean })[]>([]);
  const [isDanThuocLoading, setIsDanThuocLoading] = useState(false);

  // State cho modal dặn thuốc
  const [isShowDanThuocModal, setIsShowDanThuocModal] = useState(false);
  const [selectedDanThuocHocSinh, setSelectedDanThuocHocSinh] = useState<IFaDanThuocItemRepsone | null>(null);
  const [danThuocActionType, setDanThuocActionType] = useState<'confirm' | 'reject'>('confirm'); // Loại hành động: xác nhận hoặc từ chối

  // State cho modal HocSinhProfile
  const [isShowHocSinhProfileModal, setIsShowHocSinhProfileModal] = useState(false);
  const [selectedHocSinhId, setSelectedHocSinhId] = useState<number>(0);

  // State cho modal Import
  const [isShowImportModal, setIsShowImportModal] = useState(false);

  // Filter states cho lịch sử y tế
  const [filterTuNgay, setFilterTuNgay] = useState<string>(moment().format('YYYY-MM-DD'));
  const [filterDenNgay, setFilterDenNgay] = useState<string>(moment().format('YYYY-MM-DD'));
  const [filterNhomNoiDung, setFilterNhomNoiDung] = useState<number>(0);
  const [filterDmTruongId, setFilterDmTruongId] = useState<number | undefined>(undefined);

  const handleReloadAsync = useCallback(async () => {

    try {
      setIsLoading(true);
      const res = await lichSuXuongPhongYTeApi.selectAll(dm_coso_id, nam_hoc, filterDmTruongId, filterTuNgay, filterDenNgay);
      if (res.is_success) {
        // Dữ liệu đã được filter theo ngày ở backend, chỉ cần filter theo nhóm nội dung
        setAllLichSuXuongPhongYTes(res.data);

        // Filter theo nhóm nội dung cho hiển thị
        let displayData = [...res.data];
        if (filterNhomNoiDung > 0) {
          displayData = displayData.filter((item: any) => {
            return item.ts_hocsinh_lichsu_yte_type_id === filterNhomNoiDung;
          });
        }

        setLichSuXuongPhongYTes(displayData);
      } else {
        NotifyHelper.Error(res.message ?? "");
      }
    } catch (error) {
      NotifyHelper.Error("Có lỗi xảy ra khi tải dữ liệu");
    } finally {
      setIsLoading(false);
    }
  }, [nam_hoc, dm_coso_id, filterTuNgay, filterDenNgay, filterNhomNoiDung, filterDmTruongId]);

  const handleExportData = async () => {
    try {
      // Sử dụng API export với file mẫu và filter theo ngày tháng
      const res = await lichSuXuongPhongYTeApi.exportToTemplate({
        nam_hoc: nam_hoc,
        dm_coso_id: dm_coso_id,
        dm_truong_id: filterDmTruongId,
        tu_ngay: filterTuNgay,
        den_ngay: filterDenNgay,
        nhom_noi_dung: filterNhomNoiDung
      });

      if (res.is_success && res.data) {
        // Mở link download trong tab mới
        window.open(res.data, '_blank');
        NotifyHelper.Success("Export thành công!");
      } else {
        NotifyHelper.Error(res.message ?? "Có lỗi xảy ra khi export dữ liệu");
      }
    } catch (error) {
      NotifyHelper.Error("Có lỗi xảy ra khi export dữ liệu");
    }
  };



  const handleReloadDanThuocAsync = useCallback(async () => {
    try {
      setIsDanThuocLoading(true);
      const today = moment().format('YYYY-MM-DD');
      const res = await danThuocApi.selectByCoso({
        tu_ngay: today,
        den_ngay: today,
        dm_coso_id: dm_coso_id,
        nam_hoc: nam_hoc
      });
      if (res.is_success) {
        // Lọc chỉ những học sinh có lịch uống thuốc trong ngày hiện tại
        const filteredData = res.data.filter((item: IFaDanThuocItemRepsone) => {
          const tuNgay = moment(item.tu_ngay);
          const denNgay = moment(item.den_ngay);
          const currentDate = moment();
          return currentDate.isBetween(tuNgay, denNgay, 'day', '[]');
        });

        setDanThuocData(filteredData);
      } else {
        NotifyHelper.Error(res.message ?? "Có lỗi khi tải dữ liệu dặn thuốc");
      }
    } catch (error) {
      NotifyHelper.Error("Có lỗi xảy ra khi tải dữ liệu dặn thuốc");
    } finally {
      setIsDanThuocLoading(false);
    }
  }, [dm_coso_id, nam_hoc]);

  // Sắp xếp dữ liệu dặn thuốc: chưa xác nhận lên trước
  const sortDanThuocData = (data: IFaDanThuocItemRepsone[]): IFaDanThuocItemRepsone[] => {
    return data.sort((a: IFaDanThuocItemRepsone, b: IFaDanThuocItemRepsone) => {
      const statusA = getDanThuocStatus(a.ts_hocsinh_id, a.id);
      const statusB = getDanThuocStatus(b.ts_hocsinh_id, b.id);

      // Thứ tự ưu tiên: pending (0) -> confirmed (1) -> rejected (2)
      const priorityMap: { [key: string]: number } = { 'pending': 0, 'confirmed': 1, 'rejected': 2 };
      const priorityA = priorityMap[statusA];
      const priorityB = priorityMap[statusB];

      if (priorityA !== priorityB) {
        return priorityA - priorityB;
      }

      // Nếu cùng trạng thái, sắp xếp theo tên học sinh
      return (a.ho_ten || '').localeCompare(b.ho_ten || '', 'vi');
    });
  };

  // Kiểm tra trạng thái xử lý của dặn thuốc (xác nhận, từ chối, hoặc chưa xử lý)
  const getDanThuocStatus = (ts_hocsinh_id: number, fa_danthuoc_id?: number): 'confirmed' | 'rejected' | 'pending' => {
    const lichSu = allLichSuXuongPhongYTes.find((lichSu: ILichSuXuongPhongYTe) => {
      const isDanThuocType = lichSu.nhom_noi_dung === "Dặn thuốc";
      const isSameStudent = lichSu.ts_hocsinh_id === ts_hocsinh_id;

      // Nếu có fa_danthuoc_id, kiểm tra chính xác theo ID dặn thuốc
      if (fa_danthuoc_id && lichSu.fa_danthuoc_id) {
        return isDanThuocType && isSameStudent && lichSu.fa_danthuoc_id === fa_danthuoc_id;
      }

      // Fallback: kiểm tra theo ngày (cho các bản ghi cũ chưa có fa_danthuoc_id)
      const today = moment().format('YYYY-MM-DD');
      const isToday = moment(lichSu.ngay_tao).format('YYYY-MM-DD') === today;
      return isToday && isDanThuocType && isSameStudent;
    });

    if (!lichSu) return 'pending';

    // Kiểm tra trang_thai để xác định trạng thái
    if (lichSu.trang_thai === 'Từ chối') return 'rejected';
    if (lichSu.trang_thai === 'Đã hoàn thành') return 'confirmed';

    // Kiểm tra dm_trangthai_yte_id nếu trang_thai không khớp
    if (lichSu.dm_trangthai_yte_id === 5) return 'rejected';
    if (lichSu.dm_trangthai_yte_id === 4) return 'confirmed';

    return 'pending';
  };

  useEffect(() => {
    handleReloadAsync();
  }, [nam_hoc, dm_coso_id, handleReloadAsync]);

  useEffect(() => {
    handleReloadDanThuocAsync();
  }, [dm_coso_id, nam_hoc, handleReloadDanThuocAsync]);

  // Reload dặn thuốc khi lịch sử y tế thay đổi để cập nhật trạng thái
  useEffect(() => {
    if (allLichSuXuongPhongYTes.length > 0) {
      // Delay một chút để đảm bảo dữ liệu đã được cập nhật
      const timer = setTimeout(() => {
        handleReloadDanThuocAsync();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [allLichSuXuongPhongYTes, handleReloadDanThuocAsync]);

  const handeDelete = async (id: number) => {
    if (
      await confirm({
        content: "Bạn có chắc chắn muốn xóa?",
        title: "Lưu ý",
        cancelButtonContent: "Không xóa",
        confirmButtonContent: "Xóa",
        confirmButtonType: "danger",
      })
    ) {
      try {
        const res = await lichSuXuongPhongYTeApi.delete(id);
        if (res.is_success) {
          NotifyHelper.Success("Success");
          handleReloadAsync();
        } else {
          NotifyHelper.Error(res.message ?? "");
        }
      } catch (error) {
        NotifyHelper.Error("Có lỗi xảy ra khi xóa");
      }
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: "flex", gap: 2, height: `${window.innerHeight - 210}px` }}>
        {/* Bảng lịch sử xuống phòng y tế - bên trái */}
        <Box sx={{ width: "65%" }}>
          {/* Filter section */}
          <Box sx={{
            display: "flex",
            gap: 2,
            alignItems: "center",
            mb: 2,
            p: 2,
            backgroundColor: "canvas.subtle",
            borderRadius: 2,
            border: "1px solid",
            borderColor: "border.muted"
          }}>
        

            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Text text="Từ ngày" sx={{ minWidth: "50px", fontSize: "13px" }} />
              <TextInput
                type="date"
                value={filterTuNgay}
                onChange={(e) => setFilterTuNgay(e.target.value)}
                sx={{ width: "140px" }}
              />
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Text text="Đến ngày" sx={{ minWidth: "55px", fontSize: "13px" }} />
              <TextInput
                type="date"
                value={filterDenNgay}
                onChange={(e) => setFilterDenNgay(e.target.value)}
                sx={{ width: "140px" }}
              />
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Text text="Loại" sx={{ minWidth: "30px", fontSize: "13px" }} />
              <Box sx={{ width: "180px" }}>
                <ComboboxLichSuYTeType
                  value={filterNhomNoiDung}
                  onValueChanged={(value) => setFilterNhomNoiDung(value)}
                  isShowClearButton={true}
                />
              </Box>
            </Box>

          </Box>

          <DataTable
            fixedLeftColumns={2}
            height={`${window.innerHeight - 280}px`}
            paging={{
              enable: true,
              pageSizeItems: [10, 20, 50, 100],
            }}
            actionComponent={
              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  alignItems: "flex-end",
                  flexWrap: "nowrap",
                  justifyContent: "space-between"
                }}
              >
                {/* ComboboxTruong sát trái */}
                <ComboboxTruong
                  value={filterDmTruongId}
                  onValueChanged={(value) => setFilterDmTruongId(value || undefined)}
                  isShowClearButton={true}
                />

                {/* Các nút còn lại sát phải */}
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Button
                    text={translate("Base.Label.Create")}
                    leadingVisual={PlusIcon}
                    variant="primary"
                    size="small"
                    onClick={() => {
                      setIsShowModal(true);
                      setSelectedId(0);
                    }}
                    disabled={isLoading}
                  />
                  <ActionMenu>
                    <ActionMenu.Button variant="default" size="small">
                      <Text text="Thao tác" />
                    </ActionMenu.Button>
                    <ActionMenu.Overlay width="auto">
                      <ActionList>
                        <ActionList.Item
                          onSelect={() => setIsShowImportModal(true)}
                        >
                          <ActionList.LeadingVisual>
                            <UploadIcon />
                          </ActionList.LeadingVisual>
                          <Text text="Import" />
                        </ActionList.Item>

                        <ActionList.Item onSelect={handleExportData}>
                          <ActionList.LeadingVisual>
                            <DownloadIcon />
                          </ActionList.LeadingVisual>
                          <Text text="Export" />
                        </ActionList.Item>
                      </ActionList>
                    </ActionMenu.Overlay>
                  </ActionMenu>
                  <Button
                    text="Base.Label.Refresh"
                    leadingVisual={SyncIcon}
                    size="small"
                    onClick={handleReloadAsync}
                    disabled={isLoading}
                  />
                </Box>
              </Box>
            }
            columns={[
              {
                id: "cmd",
                width: "50px",
                caption: "#",
                align: "center",
                cellRender: (data: ILichSuXuongPhongYTe) => {
                  return (
                    <ActionMenu>
                      <ActionMenu.Anchor>
                        <IconButton
                          icon={KebabHorizontalIcon}
                          variant="invisible"
                          aria-label="Open column options"
                        />
                      </ActionMenu.Anchor>

                      <ActionMenu.Overlay>
                        <ActionList>

                          <ActionList.Item
                            onSelect={() => {
                              setIsShowModal(true);
                              if (data.id !== undefined) {
                                setSelectedId(data.id);
                              }
                            }}
                          >
                            <ActionList.LeadingVisual>
                              <PencilIcon />
                            </ActionList.LeadingVisual>
                            <TextTranslated value="Base.Label.Edit" />
                          </ActionList.Item>

                          <ActionList.Item
                            variant="danger"
                            onSelect={() => {
                              if (data.id !== undefined) {
                                handeDelete(data.id);
                              }
                            }}
                          >
                            <ActionList.LeadingVisual>
                              <TrashIcon />
                            </ActionList.LeadingVisual>
                            <TextTranslated value="Base.Label.Delete" />
                          </ActionList.Item>
                        </ActionList>
                      </ActionMenu.Overlay>
                    </ActionMenu>
                  );
                },
              },
               {
                dataField: "nhom_noi_dung",
                width: "100px",
                caption: `Loại`,
                cellRender: (data: ILichSuXuongPhongYTe) => {
                  return (
                    <Box sx={{ fontSize: "12px" }}>
                      <TextWithEllipse text={data.nhom_noi_dung || "-"} lineNumber={2} />
                      {/* Hiển thị loại dặn thuốc nếu là dặn thuốc */}
                      {data.nhom_noi_dung === "Dặn thuốc" && data.fa_danthuoc_type_ten && (
                        <Box sx={{
                          mt: 0.5,
                          fontSize: "11px",
                          color: "accent.fg",
                          fontStyle: "italic"
                        }}>
                          ({data.fa_danthuoc_type_ten})
                        </Box>
                      )}
                    </Box>
                  );
                },
              },
               {
                dataField: "trang_thai",
                width: 120,
                align: "center",
                caption: "Trạng thái",
                fixed: true,
                cellRender: (data: ILichSuXuongPhongYTe) => {
                  const trangThai = data.trang_thai || "";

                  // Xác định variant và màu sắc dựa trên trạng thái
                  let variant: "default" | "primary" | "secondary" | "accent" | "success" | "attention" | "severe" | "danger" | "done" | "sponsors" = "default";

                  switch (trangThai.toLowerCase()) {
                    case "đã hoàn thành":
                    case "hoàn thành":
                      variant = "success";
                      break;
                    case "từ chối":
                      variant = "danger";
                      break;
                    case "đang xử lý":
                    case "chờ xử lý":
                      variant = "attention";
                      break;
                    case "tạo mới":
                      variant = "primary";
                      break;
                    default:
                      variant = "default";
                  }

                  return (
                    <Label variant={variant} size="small">
                      {trangThai || "Chưa xác định"}
                    </Label>
                  );
                }
              },
              {
                dataField: "ma_hs",
                width: 120,
                align: "center",
                caption: "Mã HS",
                fixed: true
              },
              {
                dataField: "ho_ten",
                caption: "Họ tên",
                width: 180,
                fixed: true,
                cellRender: (data: ILichSuXuongPhongYTe) => (
                  <Box                    
                  >
                    <HocSinhShortInfo
                      data={data} onClick={() => {
                        setSelectedHocSinhId(data.ts_hocsinh_id);
                        setIsShowHocSinhProfileModal(true);
                      }}
                    />
                  </Box>
                ),
              },
              {
                dataField: "ten_lop",
                caption: "Lớp - Hệ",
                width: 120,
                cellRender: (data: ILichSuXuongPhongYTe) => {
                  return (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "baseline",
                      }}
                    >
                      <Box sx={{ display: "flex" }}>
                        <Box sx={{ fontWeight: 600, fontSize: "12px" }}>{data.ten_lop}</Box>
                      </Box>
                      <Box sx={{ color: "fg.muted", fontSize: "11px" }}>
                        Hệ {data.ten_he}
                      </Box>
                    </Box>
                  );
                },
              },
              {
                id: "hinh_anh",
                width: "100px",
                caption: `Hình ảnh`,
                cellRender: (data: ILichSuXuongPhongYTe) => {
                  const images = data.images || [];
                  return (
                    <Box sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      justifyContent: "center",
                      alignItems: "center",
                      minHeight: "50px",
                      height: "100%",
                      gap: 1
                    }}>
                      {images.filter((imageUrl: string) => imageUrl).map((imageUrl: string, idx: number) => {
                        return (
                          <Box key={idx}>
                            <Box
                              onClick={() => setPreviewImage(imageUrl)}
                              sx={{ cursor: "pointer" }}
                            >
                              <img src={imageUrl} alt='Image'
                                style={{
                                  height: "40px",
                                  width: "auto",
                                  borderRadius: "2px"
                                }}
                              />
                            </Box>
                          </Box>
                        );
                      })}
                      {images.length === 0 && (
                        <Box sx={{ color: "fg.muted", fontSize: 0 }}>
                          Không có ảnh
                        </Box>
                      )}
                    </Box>
                  );
                }
              },

             
              // {
              //   dataField: "ket_qua",
              //   width: "200px",
              //   caption: `Kết quả`,
              //   cellRender: (data: ILichSuXuongPhongYTe) => {
              //     return (
              //       <Box
              //         sx={{
              //           whiteSpace: "pre-wrap",
              //           wordBreak: "break-word",
              //           fontSize: "12px"
              //         }}
              //       >
              //         <TextWithEllipse text={data.ket_qua || ""} lineNumber={2} allowToggleText={true} />
              //       </Box>
              //     );
              //   },
              // },
              {
                dataField: "ngay_gio_kham",
                width: "140px",
                caption: `Ngày giờ khám`,
                cellRender: (data: ILichSuXuongPhongYTe) => {
                  return (
                    <Box sx={{ whiteSpace: "nowrap", fontSize: "12px" }}>
                      {data.ngay_gio_kham ? moment(data.ngay_gio_kham).format("DD/MM/YY HH:mm") : "-"}
                    </Box>
                  );
                },
              },
              {
                dataField: "ngay_gio_tra_ve",
                width: "140px",
                caption: `Ngày giờ trả về`,
                cellRender: (data: ILichSuXuongPhongYTe) => {
                  return (
                    <Box sx={{ whiteSpace: "nowrap", fontSize: "12px" }}>
                      {data.ngay_gio_tra_ve ? moment(data.ngay_gio_tra_ve).format("DD/MM/YY HH:mm") : "-"}
                    </Box>
                  );
                },
              },
              {
                dataField: "ly_do_xuong_phong_y_te",
                width: "200px",
                caption: `Lý do xuống phòng y tế`,
                cellRender: (data: ILichSuXuongPhongYTe) => {
                  return (
                    <Box sx={{ fontSize: "12px" }}>
                      <TextWithEllipse text={data.ly_do_xuong_phong_y_te || "-"} lineNumber={2} allowToggleText={true} />
                    </Box>
                  );
                },
              },
                  {
                dataField: "dien_bien_tinh_trang",
                width: "200px",
                caption: `Diễn biến tình trạng`,
                cellRender: (data: ILichSuXuongPhongYTe) => {
                  return (
                    <Box sx={{ fontSize: "12px" }}>
                      <TextWithEllipse text={data.tinh_trang || "-"} lineNumber={2} allowToggleText={true} />
                    </Box>
                  );
                },
              },
              {
                dataField: "xu_tri_y_te_voi_hoc_sinh",
                width: "200px",
                caption: `Xử trí y tế`,
                cellRender: (data: ILichSuXuongPhongYTe) => {
                  return (
                    <Box sx={{ fontSize: "12px" }}>
                      <TextWithEllipse text={data.xu_tri_y_te_voi_hoc_sinh || "-"} lineNumber={2} allowToggleText={true} />
                    </Box>
                  );
                },
              },
                {
                dataField: "loai_thuoc",
                width: "200px",
                caption: `Loại thuốc`,
                cellRender: (data: ILichSuXuongPhongYTe) => {
                  return (
                    <Box sx={{ fontSize: "12px" }}>
                      <TextWithEllipse text={data.loai_thuoc || "-"} lineNumber={2} allowToggleText={true} />
                    </Box>
                  );
                },
              },
              {
                id: "lien_he_info",
                width: "180px",
                caption: `Liên hệ`,
                cellRender: (data: ILichSuXuongPhongYTe) => {
                  return (
                    <Box sx={{ fontSize: "12px" }}>
                      {data.lien_he_phhs && (
                        <Box sx={{ mb: 0.5 }}>
                          <Box sx={{ fontWeight: 600, color: "accent.fg" }}>PHHS:</Box>
                          <TextWithEllipse text={data.lien_he_phhs} lineNumber={1} />
                        </Box>
                      )}
                      {data.lien_he_gvcn && (
                        <Box>
                          <Box sx={{ fontWeight: 600, color: "accent.fg" }}>GVCN:</Box>
                          <TextWithEllipse text={data.lien_he_gvcn} lineNumber={1} />
                        </Box>
                      )}
                      {!data.lien_he_phhs && !data.lien_he_gvcn && "-"}
                    </Box>
                  );
                },
              },
              {
                dataField: "chuyen_co_so_y_te",
                width: "180px",
                caption: `Chuyển cơ sở y tế`,
                cellRender: (data: ILichSuXuongPhongYTe) => {
                  return (
                    <Box sx={{ fontSize: "12px" }}>
                      <TextWithEllipse text={data.chuyen_co_so_y_te || "-"} lineNumber={2} allowToggleText={true} />
                    </Box>
                  );
                },
              },
              {
                dataField: "truong_hop_dac_biet",
                width: "180px",
                caption: `Trường hợp đặc biệt`,
                cellRender: (data: ILichSuXuongPhongYTe) => {
                  return (
                    <Box sx={{ fontSize: "12px" }}>
                      <TextWithEllipse text={data.truong_hop_dac_biet || "-"} lineNumber={2} allowToggleText={true} />
                    </Box>
                  );
                },
              },
              {
                dataField: "ten_chuyenkhoa",
                width: "150px",
                caption: `Chuyên khoa`,
                cellRender: (data: ILichSuXuongPhongYTe) => {
                  return (
                    <Box sx={{ fontSize: "12px" }}>
                      <TextWithEllipse text={data.ten_chuyenkhoa || "-"} lineNumber={1} />
                    </Box>
                  );
                },
              },

              {
                dataField: "ngay_tao",
                width: "100px",
                caption: `Ngày tạo`,
                cellRender: (data: ILichSuXuongPhongYTe) => {
                  return (
                    <Box sx={{ whiteSpace: "nowrap", fontSize: "12px" }}>
                      {moment(data.ngay_tao).format("DD/MM/YY") || ""}
                    </Box>
                  );
                },
              },
            ]}
            data={lichSuXuongPhongYTes} 
            searchEnable
          />
        </Box>

        {/* Bảng lịch dặn thuốc trong ngày - bên phải */}
        <Box sx={{
          width: "35%",
          height: `${window.innerHeight - 140}px`,

          border: "1px solid",
          borderColor: "border.muted",
          borderRadius: 2,
          backgroundColor: "canvas.default",
          display: "flex",
          flexDirection: "column"
        }}>
          {/* Header */}
          <Box sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 2,
            borderBottom: "1px solid",
            borderColor: "border.muted",
            backgroundColor: "canvas.subtle"
          }}>
            <Box>
              <PrimerText sx={{ fontSize: 2, fontWeight: 600, marginRight: 2 }}>
                Dặn thuốc trong ngày
              </PrimerText>
              <Box sx={{
                backgroundColor: "#ff6b35",
                color: "white",
                px: 2,
                py: 0.5,
                borderRadius: "12px",
                fontSize: 1,
                fontWeight: 600,
                display: "inline-flex",
                alignItems: "center"
              }}>
                {danThuocData.length}
              </Box>
            </Box>
            <Button
              text="Base.Label.Refresh"
              leadingVisual={SyncIcon}
              size="medium"
              onClick={handleReloadDanThuocAsync}
              disabled={isDanThuocLoading}
            />
          </Box>

          {/* Cards Container */}
          <Box sx={{
            overflowY: "auto",
            p: 2,
            flex: 1
          }}>
            {isDanThuocLoading ? (
              <Box sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "200px"
              }}>
                <PrimerText sx={{ color: "fg.muted" }}>Đang tải...</PrimerText>
              </Box>
            ) : danThuocData.length === 0 ? (
              <Box sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "200px"
              }}>
                <PrimerText sx={{ color: "fg.muted" }}>Không có dữ liệu</PrimerText>
              </Box>
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {sortDanThuocData([...danThuocData]).map((data, index) => {
                  const status = getDanThuocStatus(data.ts_hocsinh_id, data.id);
                  const isConfirmed = status === 'confirmed';
                  const isRejected = status === 'rejected';
                  const images = data.hinhAnhs || [];

                  return (
                    <Box key={index} sx={{
                      border: "1px solid",
                      borderColor: isConfirmed ? "success.muted" : isRejected ? "danger.muted" : "border.muted",
                      borderRadius: 2,
                      p: 2,
                      backgroundColor: isConfirmed ? "success.subtle" : isRejected ? "danger.subtle" : "canvas.subtle",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        borderColor: isConfirmed ? "success.emphasis" : isRejected ? "danger.emphasis" : "accent.muted",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.1)"
                      }
                    }}>
                      {/* Header với thông tin học sinh */}
                      <Box sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        mb: 1
                      }}>
                        <Box sx={{ flex: 1 }}>
                          <Box sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 2,
                            mb: 1
                          }}>
                         
                            <Box
                              onClick={() => {
                                setSelectedHocSinhId(data.ts_hocsinh_id);
                                setIsShowHocSinhProfileModal(true);
                              }}
                              sx={{ cursor: "pointer" }}
                            >
                              <HocSinhShortInfoWithClass data={data} />
                            </Box>
                          </Box>                         
                        </Box>

                        {isConfirmed ? (
                          <Button
                            text="Đã xác nhận"
                            variant="default"
                            size="small"
                            disabled={true}
                            sx={{
                              backgroundColor: "success.subtle",
                              color: "success.fg",
                              cursor: "not-allowed"
                            }}
                          />
                        ) : isRejected ? (
                          <Button
                            text="Đã từ chối"
                            variant="default"
                            size="small"
                            disabled={true}
                            sx={{
                              backgroundColor: "danger.subtle",
                              color: "danger.fg",
                              cursor: "not-allowed"
                            }}
                          />
                        ) : (
                          <Box sx={{ display: "flex", gap: 1 }}>
                            <Button
                              text="Xác nhận"
                              variant="primary"
                              size="small"
                              onClick={() => {
                                setSelectedDanThuocHocSinh(data);
                                setDanThuocActionType('confirm');
                                setIsShowDanThuocModal(true);
                              }}
                            />
                            <Button
                              text="Từ chối"
                              variant="danger"
                              size="small"
                              onClick={() => {
                                setSelectedDanThuocHocSinh(data);
                                setDanThuocActionType('reject');
                                setIsShowDanThuocModal(true);
                              }}
                            />
                          </Box>
                        )}
                      </Box>

                      {/* Thông tin ngày và thuốc */}
                      <Box sx={{
                        p: 2,
                        backgroundColor: "canvas.default",
                        borderRadius: 1,
                        border: "1px solid",
                        borderColor: "border.muted",
                        mb: 1
                      }}>
                        {/* Thời gian dặn thuốc */}
                        <Box sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mb: 2
                        }}>
                          <PrimerText sx={{ fontSize: 1, color: "fg.muted" }}>
                            Thời gian:
                          </PrimerText>
                          <PrimerText sx={{ fontSize: 1, fontWeight: 600 }}>
                            {moment(data.tu_ngay).format("DD/MM/YYYY")}
                          </PrimerText>
                          <PrimerText sx={{ fontSize: 1, color: "fg.muted" }}>
                            đến
                          </PrimerText>
                          <PrimerText sx={{ fontSize: 1, fontWeight: 600 }}>
                            {moment(data.den_ngay).format("DD/MM/YYYY")}
                          </PrimerText>
                        </Box>

                        {/* Loại dặn thuốc */}
                        {data.type_ten && (
                          <Box sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            mb: 2
                          }}>
                            <PrimerText sx={{ fontSize: 1, color: "fg.muted" }}>
                              Loại:
                            </PrimerText>
                            <Label variant="accent" size="small">
                              {data.type_ten}
                            </Label>
                          </Box>
                        )}

                        {/* Chi tiết thuốc */}
                        {data.chiTiets && data.chiTiets.length > 0 ? (
                          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                            {data.chiTiets.slice(0, 3).map((chiTiet, idx) => (
                              <Box key={idx} sx={{
                                p: 1.5,
                                borderRadius: 1,
                                backgroundColor: "canvas.subtle"
                              }}>
                                <PrimerText sx={{
                                  fontSize: 1,
                                  fontWeight: 600,
                                  color: "accent.fg",
                                  mb: 0.5
                                }}>
                                  <TextWithEllipse text={chiTiet.ten_thuoc} lineNumber={1} />
                                </PrimerText>
                                <PrimerText sx={{
                                  fontSize: 0,
                                  color: "fg.muted"
                                }}>
                                  <TextWithEllipse text={chiTiet.cachdung_lieuluong} lineNumber={2} />
                                </PrimerText>
                              </Box>
                            ))}
                            {data.chiTiets.length > 3 && (
                              <PrimerText sx={{
                                fontSize: 0,
                                color: "fg.muted",
                                fontStyle: "italic",
                                textAlign: "center",
                                mt: 1
                              }}>
                                +{data.chiTiets.length - 3} thuốc khác...
                              </PrimerText>
                            )}
                          </Box>
                        ) : (
                          <PrimerText sx={{
                            fontSize: 1,
                            color: "fg.muted",
                            fontStyle: "italic"
                          }}>
                            Chưa có chi tiết thuốc
                          </PrimerText>
                        )}
                      </Box>

                      {/* Hình ảnh */}
                      {images.length > 0 && (
                        <Box>                       
                          <Box sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 1
                          }}>
                            {images.filter((imageItem: any) => imageItem.url_hinh_anh).map((imageItem: any, idx: number) => (
                              <Box key={idx}>
                                <Box
                                  onClick={() => setPreviewImage(imageItem.url_hinh_anh)}
                                  sx={{
                                    cursor: "pointer",
                                    border: "1px solid",
                                    borderColor: "border.muted",
                                    borderRadius: 1,
                                    overflow: "hidden",
                                    "&:hover": {
                                      borderColor: "accent.muted"
                                    }
                                  }}
                                >
                                  <img
                                    src={imageItem.url_hinh_anh}
                                    alt='Medicine Image'
                                    style={{
                                      height: "60px",
                                      width: "60px",
                                      objectFit: "cover"
                                    }}
                                  />
                                </Box>
                              </Box>
                            ))}
                          </Box>
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      {isShowModal && (
        <LichSuXuongPhongYTeModal
          id={selectedId}
          onClose={() => {
            handleReloadAsync();
            setIsShowModal(false);
          }}
          onSuccess={() => {
            handleReloadAsync();
            setIsShowModal(false);
          }}
        />
      )}

      {/* Modal xác nhận/từ chối dặn thuốc */}
      {isShowDanThuocModal && selectedDanThuocHocSinh && (
        <LichSuXuongPhongYTeModal
          id={0} // Tạo mới
          onClose={() => {
            setIsShowDanThuocModal(false);
            setSelectedDanThuocHocSinh(null);
            setDanThuocActionType('confirm'); // Reset về mặc định
          }}
          onSuccess={() => {
            // Reload lịch sử y tế trước để cập nhật trạng thái
            handleReloadAsync().then(() => {
              // Sau đó reload dặn thuốc để cập nhật UI
              handleReloadDanThuocAsync();
            });
            setIsShowDanThuocModal(false);
            setSelectedDanThuocHocSinh(null);
            setDanThuocActionType('confirm'); // Reset về mặc định
          }}
          // Truyền thông tin học sinh để pre-fill
          prefilledData={{
            ts_hocsinh_id: selectedDanThuocHocSinh.ts_hocsinh_id,
            ho_ten: selectedDanThuocHocSinh.ho_ten,
            ma_hs: selectedDanThuocHocSinh.ma_hs,
            ten_lop: selectedDanThuocHocSinh.ten_lop,
            ten_khoi: selectedDanThuocHocSinh.ten_khoi,
            ten_he: selectedDanThuocHocSinh.ten_he,
            danThuocInfo: selectedDanThuocHocSinh,
            actionType: danThuocActionType // Truyền loại hành động
          }}
        />
      )}

      {/* Modal HocSinhProfile */}
      {isShowHocSinhProfileModal && selectedHocSinhId > 0 && (
        <HocSinhProfile
          id={selectedHocSinhId}
          onClose={() => {
            setIsShowHocSinhProfileModal(false);
          }}
          onSuccess={() => {
            setIsShowHocSinhProfileModal(false);
          }}
        />
      )}

      {/* Modal preview ảnh */}
      {previewImage && (
        <Dialog
          isOpen={!!previewImage}
          onDismiss={() => setPreviewImage(null)}
          aria-labelledby="preview-image-title"
        >
          <Dialog.Header id="preview-image-title">
            Xem ảnh
          </Dialog.Header>
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <img
              src={previewImage}
              alt="Preview"
              style={{
                maxWidth: '100%',
                maxHeight: '70vh',
                objectFit: 'contain'
              }}
            />
          </Box>
        </Dialog>
      )}

      {/* Modal Import */}
      {isShowImportModal && (
        <ImportLichSuYTeModal
          animationOf="import-lich-su-y-te-modal"
          onClose={() => {
            setIsShowImportModal(false);
            handleReloadAsync();
          }}
        />
      )}
    </Box>
  );
};

export default LichSuXuongPhongYTePage;
